export type KeybindingOverrideMap = Record<string, string | null>;

export interface KeybindingOverrides {
	overrides: KeybindingOverrideMap;
	version: number;
}

const storageKey = "keyboard.overrides";

export const keybindingOverridesChangeEvent = "worksuite:keybinding-overrides-change";

export const keybindingOverridesVersion = 1;

export const defaultKeybindingOverrides: KeybindingOverrides = {
	overrides: {},
	version: keybindingOverridesVersion
};

const isOverrideValue = (value: unknown): value is string | null => value === null || typeof value === "string";

const sanitizeOverrideMap = (value: unknown): KeybindingOverrideMap => {
	if (typeof value !== "object" || value === null) return {};
	const sanitized: KeybindingOverrideMap = {};
	for (const [actionId, binding] of Object.entries(value)) {
		if (isOverrideValue(binding)) sanitized[actionId] = binding;
	}
	return sanitized;
};

export const readKeybindingOverrides = (): KeybindingOverrides => {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return defaultKeybindingOverrides;
		const parsed = JSON.parse(raw) as Partial<KeybindingOverrides>;
		return { overrides: sanitizeOverrideMap(parsed.overrides), version: keybindingOverridesVersion };
	} catch {
		return defaultKeybindingOverrides;
	}
};

export const writeKeybindingOverrides = (value: KeybindingOverrides): void => {
	try {
		localStorage.setItem(storageKey, JSON.stringify(value));
	} catch {
		// QuotaExceededError — storage full, keep the in-memory overrides
	}
};

export const effectiveBinding = (defaultBindings: string[], override: string | null | undefined): string[] => {
	if (override === undefined) return defaultBindings;
	if (override === null) return [];
	return [override];
};

export const setOverrideValue = (
	overrides: KeybindingOverrideMap,
	actionId: string,
	binding: string | null
): KeybindingOverrideMap => ({ ...overrides, [actionId]: binding });

export const clearOverrideValue = (overrides: KeybindingOverrideMap, actionId: string): KeybindingOverrideMap => {
	const { [actionId]: _removed, ...rest } = overrides;
	return rest;
};
